/** PC/모바일 공용 한 손 액션 독. 제품 화면과 실물 검사가 같은 배선을 부른다. */
import { $ } from '../core/state.js';
import { observeUiHeight } from './readability.js';
import { MOBILE_TILE_STEPS, getMobileTilePreference, setMobileTilePreference } from '../world/map.js';
import { isoZoomState, isoZoomStep } from '../render/iso-zoom.js';

let ready = false;
/** R245 — 쿼터뷰에서 후보가 하나뿐일 때(세로 휴대폰 등) 「시야」 창에 한 줄 — 눌러도 아무 일 없는 버튼이 고장으로 읽히지 않게. */
const ISO_ZOOM_FIXED_NOTE = '이 화면 크기에서는 시야가 고정이다(원거리 사거리가 늘 화면 안에 들게)';

export function initActionDock() {
  if (ready) return;
  ready = true;

  const actions = $('#actions');
  const autoMenu = $('#auto-more');
  const zoomOut = $('#zoom-out');
  const zoomIn = $('#zoom-in');
  /**
   * R245(레드팀 C2-17) — 쿼터뷰가 그리는 동안 「시야」는 도트 배율 후보를 한 칸씩 옮긴다(`render/iso-zoom.js`). 탑다운 칸 크기 선호(18·24·32)는
   * 쿼터뷰에서 같은 배율로 떨어져 버튼이 무반응이었다. 끝 단계면 그 버튼을 끄고, 후보가 하나뿐이면 둘 다 끄고 한 줄로 이유를 말한다.
   */
  let note = null;
  const updateZoom = () => {
    const iso = isoZoomState();
    if (iso.active) {
      if (zoomOut) zoomOut.disabled = !iso.canOut;
      if (zoomIn) zoomIn.disabled = !iso.canIn;
      const box = zoomOut?.parentElement;
      if (box && iso.fixed && !note) {
        note = document.createElement('p'); note.id = 'zoom-note'; note.textContent = ISO_ZOOM_FIXED_NOTE;
        note.style.cssText = 'grid-column:1/-1;margin:2px 0 0;max-width:150px;font-size:var(--ui-note,11px);line-height:1.3;color:var(--muted,#b9ad95)';
        box.appendChild(note);
      } else if (note && !iso.fixed) { note.remove(); note = null; }
      return;
    }
    if (note) { note.remove(); note = null; }
    const index = MOBILE_TILE_STEPS.indexOf(getMobileTilePreference());
    if (zoomOut) zoomOut.disabled = index <= 0;
    if (zoomIn) zoomIn.disabled = index >= MOBILE_TILE_STEPS.length - 1;
  };
  if (zoomOut) zoomOut.onclick = () => {
    if (isoZoomState().active) { isoZoomStep(-1); updateZoom(); return; }
    const index = MOBILE_TILE_STEPS.indexOf(getMobileTilePreference());
    if (index > 0) setMobileTilePreference(MOBILE_TILE_STEPS[index - 1]);
    updateZoom();
  };
  if (zoomIn) zoomIn.onclick = () => {
    if (isoZoomState().active) { isoZoomStep(1); updateZoom(); return; }
    const index = MOBILE_TILE_STEPS.indexOf(getMobileTilePreference());
    if (index < MOBILE_TILE_STEPS.length - 1) setMobileTilePreference(MOBILE_TILE_STEPS[index + 1]);
    updateZoom();
  };
  // 쿼터뷰가 켜지거나(첫 프레임) 화면을 돌려 후보가 바뀌면 버튼 상태를 다시 맞춘다
  addEventListener('unige:iso-zoom', updateZoom);
  updateZoom();
  /**
   * Q276(사용자 결정 2026-10-13 B안) — [더보기] 팔레트는 없앴다. 거기 있던 것은 각자 한 곳으로 갔다:
   * 파티·설정·도움말·나가기·후원 → 메뉴 한 화면 · 채팅 → [기록] › [말하기] · 소리 → 설정 · 강공 → [자동] 위 작은 창 · 광고 보상 → 메뉴 화면.
   * [자동] 위 작은 창(자동 공격·자동 반격·강공)은 독 높이를 바꾸지 않는다. 세계를 다시 누르거나 Escape 면 닫힌다.
   */
  const closeAuto = () => { actions?.classList.remove('auto-open'); autoMenu?.setAttribute('aria-hidden', 'true'); };
  document.addEventListener('pointerdown', (e) => {
    const view = $('#view-options');
    if (view?.open && !view.contains(e.target)) view.open = false;
    if (actions?.classList.contains('auto-open') && !actions.contains(e.target)) closeAuto();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const view = $('#view-options'); if (view) view.open = false;
      const crime = $('#crimeself details'); if (crime) crime.open = false;
    }
    if (e.key === 'Escape' && actions?.classList.contains('auto-open')) closeAuto();
  });
  // 패널은 클릭뿐 아니라 단축키·NPC·내부 링크에서도 열린다. 그때 작은 창이 패널
  // 뒤에서 aria-hidden=false 인 채 남으면 보이지 않는 버튼이 키보드/리더에는
  // 계속 열려 있다. 패널 오케스트레이터가 보내는 한 신호로 모든 진입로를 정리한다.
  addEventListener('unige:panel-opened', closeAuto);

  /**
   * 하단 바의 높이는 안전 영역·글꼴에 따라 달라진다(폰은 행동 줄 + 늘 보이는 다섯 칸 두 줄 — Q276). [자동] 위 작은 창은
   * absolute 라 높이에 끼지 않는다. 실제 독 높이를 `--barh` 하나에 계속 반영한다.
   * 로그·토스트·패널·스킬바가 이 값만 읽어 서로 겹치지 않는다.
   */
  if (actions) observeUiHeight(actions, '--barh', false, window.ResizeObserver);
  // 키보드와 주소창이 실제로 가린 높이는 layout viewport와 다르다.
  const viewport = window.visualViewport;
  const fitPanel = () => {
    document.documentElement.style.setProperty('--panel-vheight', `${viewport?.height ?? window.innerHeight}px`);
    document.documentElement.style.setProperty('--panel-vtop', `${viewport?.offsetTop ?? 0}px`);
    // The sheet is anchored at the bottom; keep it above the keyboard as well.
    document.documentElement.style.setProperty('--panel-vbottom', `${Math.max(0,
      window.innerHeight - (viewport?.height ?? window.innerHeight) - (viewport?.offsetTop ?? 0))}px`);
  };
  viewport?.addEventListener('resize', fitPanel);
  viewport?.addEventListener('scroll', fitPanel);
  window.addEventListener('resize', fitPanel);
  fitPanel();
}
